import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box } from '@mui/material';

import { useStudio } from '../../queries';
import { ApiResponseError } from '../../api/envelope';

import { TopBar } from '../shared/topbar/TopBar';
import { SwatchPanel } from '../shared/panels/SwatchPanel';
import { SelectionChips } from '../shared/panels/SelectionChips';
import { LabLoadingSkeleton } from '../shared/layout/LabLoadingSkeleton';
import { ActionPanel } from '../shared/panels/ActionPanel';
import { AgentBar } from '../shared/agent-bar/AgentBar';
import { RailSplitter } from '../shared/layout/RailSplitter';
import { loadRailWidth, saveRailWidth } from '../shared/layout/railWidths';
import { buildStudioMetadata, type SwatchSelections } from '../shared/panels/swatchSelection';
import { useMobileShell } from '../../shell/useMobileShell';
import { MobileShell } from '../../shell/MobileShell';
import { useAnimateHandoffStore } from '../../store/animateHandoffStore';
import type { VideoOperationType } from '../../domain';

import { ClipCanvas } from './canvas/ClipCanvas';
import {
  AnimationOperationPanel,
  DEFAULT_CONTROLS,
  OperationDock,
  pickDefaultOperation,
  StudioRequestContext,
  useVideoOperationMutation,
  type PanelPrefill,
  type VideoControls,
} from './operations';
import { useNairaRate } from '../../credits/useNairaRate';

interface Props {
  studioId?: string;
}

/**
 * Animation Lab shell (A-F6) — parity with Image/Writer Lab: TopBar · Swatch
 * rail · ClipCanvas (center) · Operations/Agents rail · Agent bar. The single
 * net-new piece vs. the other labs is async-job polling, owned by
 * `useVideoOperationMutation`, which drives the canvas through
 * empty→rendering→ready/failed.
 */
export const AnimationLab: React.FC<Props> = ({ studioId = 'motion' }) => {
  const { data: studio, isLoading, error } = useStudio(studioId);
  const { isMobileShell } = useMobileShell();

  const [activeKind, setActiveKind] = useState<VideoOperationType | null>(null);
  const [controls, setControls] = useState<VideoControls>(DEFAULT_CONTROLS);
  const [swatchSelections, setSwatchSelections] = useState<SwatchSelections>({});
  const [prefill, setPrefill] = useState<PanelPrefill | undefined>(undefined);
  const [posterUrl, setPosterUrl] = useState<string | undefined>(undefined);

  const [leftRailWidth, setLeftRailWidth] = useState(() => loadRailWidth('left'));
  const [rightRailWidth, setRightRailWidth] = useState(() => loadRailWidth('right'));

  const { submit, cancel, reset, checkNow, stalled, isRendering, job, status, error: jobError } =
    useVideoOperationMutation();
  // The finished clip's cost in Naira too: the wallet's rate for the model it ran on.
  const clipNairaRate = useNairaRate('video', status === 'Completed' ? job?.result?.modelUsed : null);

  // Reset per-studio UI state on studio switch.
  useEffect(() => {
    setSwatchSelections({});
    setActiveKind(null);
  }, [studioId]);

  // Default the active operation from the studio's featured list.
  const defaultKind = useMemo(() => (studio ? pickDefaultOperation(studio) : null), [studio]);
  useEffect(() => {
    if (defaultKind && !activeKind) setActiveKind(defaultKind);
  }, [defaultKind, activeKind]);

  // A-F7: consume a pending "Animate this still" handoff → open Image→Video
  // pre-filled with the source, and show the still as the canvas poster.
  const pendingAnimate = useAnimateHandoffStore(s => s.pending);
  useEffect(() => {
    if (!pendingAnimate) return;
    const consumed = useAnimateHandoffStore.getState().consumeHandoff();
    if (!consumed) return;
    setPrefill({ sourceImageId: consumed.sourceAssetId, sourceImageUrl: consumed.sourceSignedUrl });
    setPosterUrl(consumed.sourceSignedUrl);
    setActiveKind('ImageToVideo');
  }, [pendingAnimate]);

  // Selection-derived studio_motion metadata block for request decoration.
  const studioRequest = useMemo(() => {
    if (!studio) return {};
    const meta = buildStudioMetadata(
      studio.metadataSchema,
      studio.parameterPalettes,
      swatchSelections,
    );
    return meta ? { studioId: studio.id, metadata: meta } : { studioId: studio.id };
  }, [studio, swatchSelections]);

  const handleSubmit = (kind: VideoOperationType, request: Parameters<typeof submit>[1]) => {
    void submit(kind, request);
  };

  if (isLoading) {
    return <LabLoadingSkeleton />;
  }

  if (error || !studio) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          {error instanceof ApiResponseError
            ? `${error.code}: ${error.message}`
            : error instanceof Error
              ? error.message
              : `Studio "${studioId}" not found.`}
        </Alert>
      </Box>
    );
  }

  const clipCanvas = (
    <ClipCanvas
      clip={status === 'Completed' ? job : undefined}
      rendering={isRendering ? job : undefined}
      errorMessage={jobError?.message}
      retryable={'retryable' in (jobError ?? {}) ? (jobError as { retryable?: boolean }).retryable : undefined}
      posterUrl={posterUrl}
      onCancel={cancel}
      onRetry={reset}
      stalled={stalled}
      onCheckNow={checkNow}
      nairaRate={clipNairaRate}
    />
  );

  const swatchesContent = (
    <SwatchPanel
      palettes={studio.parameterPalettes}
      selections={swatchSelections}
      onSelectionsChange={setSwatchSelections}
    />
  );

  const operationsContent = (
    <>
      <SelectionChips
        palettes={studio.parameterPalettes}
        selections={swatchSelections}
        onSelectionsChange={setSwatchSelections}
      />
      {activeKind && (
        <Box sx={{ p: 1 }}>
          <StudioRequestContext.Provider value={studioRequest}>
            <AnimationOperationPanel
              kind={activeKind}
              controls={controls}
              onControlsChange={setControls}
              onSubmit={handleSubmit}
              isRendering={isRendering}
              prefill={prefill}
            />
          </StudioRequestContext.Provider>
        </Box>
      )}
      {activeKind && (
        <Box sx={{ mt: 'auto', borderTop: theme => `1px solid ${theme.palette.divider}` }}>
          <OperationDock studio={studio} activeKind={activeKind} onSelect={setActiveKind} />
        </Box>
      )}
    </>
  );

  const agentBar = (
    <AgentBar
      sessionId={job?.sessionId}
      labId={studio.labId}
      labName={studio.labId === 'animation-lab' ? 'Animation Lab' : studio.labId}
      studioId={studioId}
      studioName={studio.name}
      defaultModel={studio.defaultLlmModel}
    />
  );

  if (isMobileShell) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <MobileShell
          title={`Animation Lab · ${studio.name}`}
          canvasSlot={clipCanvas}
          swatchesSlot={<Box sx={{ p: 1 }}>{swatchesContent}</Box>}
          actionSlot={operationsContent}
          agentBarSlot={agentBar}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <TopBar labId={studio.labId} studioId={studioId} />
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            width: leftRailWidth,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: theme => `1px solid ${theme.palette.divider}`,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {swatchesContent}
        </Box>

        <RailSplitter
          side="left"
          width={leftRailWidth}
          onChange={setLeftRailWidth}
          onCommit={w => saveRailWidth('left', w)}
        />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>{clipCanvas}</Box>

        <RailSplitter
          side="right"
          width={rightRailWidth}
          onChange={setRightRailWidth}
          onCommit={w => saveRailWidth('right', w)}
        />

        <ActionPanel
          studio={studio}
          width={rightRailWidth}
          defaultAgentInstruction={`Help me animate a ${studio.name} clip.`}
          operationsContent={operationsContent}
        />
      </Box>

      {agentBar}
    </Box>
  );
};
